import React, { useState } from 'react';
import { Box, Button, Grid, List, ListItem, Typography, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MerchantApplicationRequest, createMerchantApplication } from '../../Api/BoardingApi';

const GetStartedForm: any = ({ paymentSolutionName, culture, businessUnitCustomerId, merchantApplicationIdentifier }:
    { paymentSolutionName: string, culture: string, businessUnitCustomerId: string, merchantApplicationIdentifier: string }) => {

    const { t } = useTranslation();
    const [formData, setFormData] = useState<MerchantApplicationRequest>({
        paymentSolutionName: paymentSolutionName,
        applicantEmailAddress: '',
        legalBusinessName: '',
        doingBusinessAs: '',
        languageCulture: culture,
        businessUnitCustomerId: businessUnitCustomerId,
        merchantApplicationIdentifier: merchantApplicationIdentifier
    });
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    let navigate = useNavigate();
    const [emptyInputShown, setEmptyInputShown] = useState<{ [key: string]: boolean }>({});

    const emailAddressDescription: string = t('GetStartedForm_inputDescription1');
    const legalBusinessNameDescription: string = t('GetStartedForm_inputDescription2');
    const doingBusinessAsDescription: string = t('GetStartedForm_inputDescription3');
    const startApplicationDescription: string = t('GetStartedForm_inputDescription4');
    const emailLabel: string = t('GetStartedForm_inputLabel1') + " *";
    const legalBusinessNameLabel: string = t('GetStartedForm_inputLabel2') + " *";
    const doingBusinessAsLabel: string = t('GetStartedForm_inputLabel3');
    const buttonText: string = t('GetStartedForm_buttonText');


    // --- Constants ---
    const MAX_LENGTHS = {
        applicantEmailAddress: 64,
        legalBusinessName: 40,
        doingBusinessAs: 40,
    };

    // --- Validation Helpers ---
    function isFieldEmpty(fieldName: string): boolean {
        return !!emptyInputShown[fieldName];
    }

    function isEmailValid(email: string): boolean {
        const trimmed = email.trim();
        if (trimmed.length === 0 && email.length > 0) return false;
        if (trimmed.length === 0) return true;
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(trimmed);
    }

    function exceedsMaxLength(value: string, maxLength: number): boolean {
        return value.length > maxLength;
    }

    function getFieldError(fieldName: string, value: string): boolean {
        const maxLength = MAX_LENGTHS[fieldName as keyof typeof MAX_LENGTHS];
        if (fieldName === 'applicantEmailAddress') {
            return (
                exceedsMaxLength(value, maxLength) ||
                (value.length > 0 && !isEmailValid(value)) ||
                isFieldEmpty(fieldName)
            );
        }
        return exceedsMaxLength(value, maxLength) || isFieldEmpty(fieldName);
    }

    function getHelperText(fieldName: string, value: string): string {
        const maxLength = MAX_LENGTHS[fieldName as keyof typeof MAX_LENGTHS];
        if (isFieldEmpty(fieldName)) {
            return t('GetStartedForm_helperTextEmptyInput');
        }
        if (fieldName === 'applicantEmailAddress' && value.length > 0 && !isEmailValid(value)) {
            return t('GetStartedForm_helperTextEmailFormat');
        }
        if (exceedsMaxLength(value, maxLength)) {
            return t('GetStartedForm_helperText').replace('{{maxLength}}', maxLength.toString());
        }
        return '';
    }

    // --- Event Handlers ---
    function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setEmptyInputShown((prev) => ({ ...prev, [name]: false }));
    }

    const handleSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();

        type RequiredField = 'applicantEmailAddress' | 'legalBusinessName';
        const requiredFields: RequiredField[] = ['applicantEmailAddress', 'legalBusinessName'];
        const newEmptyInputShown: { [key: string]: boolean } = {};
        let hasEmpty = false;

        requiredFields.forEach((field) => {
            if (formData[field].trim().length === 0) {
                newEmptyInputShown[field] = true;
                hasEmpty = true;
            }
        });

        if (hasEmpty) {
            setEmptyInputShown((prev) => ({ ...prev, ...newEmptyInputShown }));
            return;
        }

        const hasValidationErrors =
            getFieldError('applicantEmailAddress', formData.applicantEmailAddress) ||
            getFieldError('legalBusinessName', formData.legalBusinessName) ||
            getFieldError('doingBusinessAs', formData.doingBusinessAs);

        if (hasValidationErrors) return;

        setButtonDisabled(true);
        setIsLoading(true);

        try {
            await createMerchantApplication(formData)
                .then((response) => {
                    setIsLoading(false);
                    if (response.data?.merchantApplicationLink) {
                        //successful: link is good, navigate to application
                        console.log("Navigating to application...");
                        window.location.href = response.data.merchantApplicationLink;
                    } else {
                        //successful: link is bad, navigate to confirmation page
                        console.log("No application link. Navigating to confirmation...");
                        navigate(`/confirmation/${paymentSolutionName}/${culture}/`);
                    }
                }).catch((error) => {
                    setIsLoading(false);
                    if (error?.status === 408 || error?.code === 'ECONNABORTED') {
                        //error: timeout, navigate to confirmation page
                        console.error("Timeout error:", error);
                        navigate(`/confirmation/${paymentSolutionName}/${culture}/`);
                    } else {
                        //error: not a timeout, navigate to error page
                        console.error("Server error:", error);
                        navigate(`/error/${paymentSolutionName}/${culture}/`);
                    }
                });
        } catch (error) {
            setIsLoading(false);
        }
    };

    return (

        <Box id="getStartedForm">
            <form onSubmit={handleSubmit}>
                <List className="reset">
                    {/* Email Field */}
                    <ListItem className="count" data-test-id="form-item-1">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body1" component="p" gutterBottom data-test-id="email-address-description"> 
                                    {emailAddressDescription}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={formData.applicantEmailAddress}
                                    onChange={handleInputChange}
                                    helperText={getHelperText('applicantEmailAddress', formData.applicantEmailAddress)}
                                    error={getFieldError('applicantEmailAddress', formData.applicantEmailAddress)}
                                    name="applicantEmailAddress"
                                    id="form-applicant-email-address-input"
                                    data-test-id="applicant-email-address-input" 
                                    label={emailLabel}
                                    variant="outlined"
                                    type="text"
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </ListItem>

                    {/* Legal Business Name Field */}
                    <ListItem className="count" data-test-id="form-item-2">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body1" component="p" gutterBottom data-test-id="business-name-description">
                                    {legalBusinessNameDescription}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={formData.legalBusinessName}
                                    onChange={handleInputChange}
                                    helperText={getHelperText('legalBusinessName', formData.legalBusinessName)}
                                    error={getFieldError('legalBusinessName', formData.legalBusinessName)}
                                    name="legalBusinessName"
                                    id="form-legal-business-name-input"
                                    data-test-id="business-name-input"
                                    label={legalBusinessNameLabel}
                                    variant="outlined"
                                    type="text"
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </ListItem>

                    {/* Doing Business As Field */}
                    <ListItem className="count" data-test-id="form-item-3">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body1" component="p" gutterBottom data-test-id="doing-business-as-description">
                                    {doingBusinessAsDescription}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    value={formData.doingBusinessAs}
                                    onChange={handleInputChange}
                                    helperText={getHelperText('doingBusinessAs', formData.doingBusinessAs)}
                                    error={getFieldError('doingBusinessAs', formData.doingBusinessAs)}
                                    name="doingBusinessAs"
                                    id="form-doing-business-as-input"
                                    data-test-id="doing-business-as-input" 
                                    label={doingBusinessAsLabel}
                                    variant="outlined"
                                    type="text"
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </ListItem>

                    {/* Submit Button */}
                    <ListItem className="count" data-test-id="form-item-4">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body1" component="p" gutterBottom data-test-id="start-application-description">
                                    {startApplicationDescription}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Box className="center-container">
                                    <Box className="center-item">
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            id="submitButton"
                                            className="fullsteam-button"
                                            data-test-id="start-application-button"
                                            disabled={buttonDisabled}
                                        >
                                            {buttonText}
                                        </Button>
                                        {isLoading && <Box className="loader" />}
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </ListItem>
                </List>
            </form>
        </Box>
    );
}

export default GetStartedForm;